import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import type { Toast } from "react-hot-toast";
import airplaneSrc from "../assets/home/homepage-airplane-primary.svg";
import packageSrc from "../assets/home/homepage-toast-package.svg";

// ── Inject keyframes ──────────────────────────────────────
const STYLE_ID = "waitlist-toast-kf-v8";
if (typeof document !== "undefined") {
  const existing = document.getElementById(STYLE_ID);
  if (existing) existing.remove();
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes wt-fly-in      { 0% { transform:translateX(-120vw); } 100% { transform:translateX(calc(50vw - 250px)); } }
    @keyframes wt-fly-out     { 0% { transform:translateX(calc(50vw - 250px)); } 100% { transform:translateX(120vw); } }
    @keyframes wt-pkg-in      { 0% { opacity:0; transform:translateY(-120px); } 100% { opacity:1; transform:translateY(0); } }
    @keyframes wt-pkg-out     { 0% { opacity:1; transform:translateY(0); } 100% { opacity:0; transform:translateY(280px); } }
    @keyframes wt-ribbon-off  { 0% { transform:scaleX(1);  opacity:1; } 100% { transform:scaleX(0);  opacity:0; } }
    @keyframes wt-ribbon-on   { 0% { transform:scaleX(0);  opacity:0; } 100% { transform:scaleX(1);  opacity:1; } }
    @keyframes wt-card-pop    { 0% { opacity:0; transform:scale(0.5) translateY(14px); } 65% { transform:scale(1.07) translateY(-4px); } 100% { opacity:1; transform:scale(1) translateY(0); } }
    @keyframes wt-card-out    { 0% { opacity:1; } 100% { opacity:0; transform:scale(0.85); } }
    @keyframes wt-flutter     { 0%,100% { transform:skewY(0deg); } 33% { transform:skewY(2deg); } 66% { transform:skewY(-1.5deg); } }
  `;
  document.head.appendChild(s);
}

// ── Timing ────────────────────────────────────────────────
const ENTER_MS = 2000;
const PAUSE_MS = 3000;
const EXIT_MS  = 1200;
const TOTAL_MS = ENTER_MS + PAUSE_MS + EXIT_MS;

// ── Shared card style ─────────────────────────────────────
const cardBase: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  background: "#ffffff",
  borderLeft: "5px solid #7A0056",
  borderRadius: 12,
  padding: "12px 22px",
  boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
  fontFamily: "'Poppins', sans-serif",
  fontSize: 14,
  fontWeight: 500,
  color: "#111827",
  whiteSpace: "nowrap",
};

const Dot = () => (
  <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#7A0056", flexShrink: 0 }} />
);

// ── Airplane overlay ──────────────────────────────────────
const PlaneOverlay: React.FC<{ message: string; onDone: () => void }> = ({ message, onDone }) => {
  const wrapRef   = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const doneRef   = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const wrap   = wrapRef.current;
    const banner = bannerRef.current;
    if (!wrap || !banner) return;

    wrap.style.animation = `wt-fly-in ${ENTER_MS}ms cubic-bezier(0.22,1,0.36,1) forwards`;

    const t1 = setTimeout(() => {
      banner.style.animation = "wt-flutter 1s ease-in-out infinite";
    }, ENTER_MS);

    const t2 = setTimeout(() => {
      banner.style.animation = "none";
      wrap.style.animation = `wt-fly-out ${EXIT_MS}ms cubic-bezier(0.55,0,1,0.45) forwards`;
    }, ENTER_MS + PAUSE_MS);

    const t3 = setTimeout(() => doneRef.current(), TOTAL_MS + 300);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []); // run once on mount only

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 100,
        left: 0,
        right: 0,
        height: 140,
        zIndex: 99999,
        pointerEvents: "none",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
      }}
    >
      <div
        ref={wrapRef}
        style={{
          display: "flex",
          alignItems: "center",
          transform: "translateX(-120vw)",
          willChange: "transform",
        }}
      >
        <div ref={bannerRef} style={{ ...cardBase, borderRadius: "10px 0 0 10px", transformOrigin: "right center" }}>
          <Dot />
          {message}
        </div>
        <div style={{ width: 28, height: 2, background: "rgba(122,0,86,0.5)", flexShrink: 0 }} />
        <img src={airplaneSrc} alt="" style={{ width: 130, height: "auto", display: "block", flexShrink: 0, transform: "rotate(5deg)" }} />
      </div>
    </div>,
    document.body
  );
};

// ── Package overlay ───────────────────────────────────────
const PackageOverlay: React.FC<{ message: string; onDone: () => void }> = ({ message, onDone }) => {
  const wrapRef   = useRef<HTMLDivElement>(null);
  const ribbonRef = useRef<HTMLDivElement>(null);
  const cardRef   = useRef<HTMLDivElement>(null);
  const doneRef   = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const wrap   = wrapRef.current;
    const ribbon = ribbonRef.current;
    const card   = cardRef.current;
    if (!wrap || !ribbon || !card) return;

    // 1. Package fades in
    wrap.style.animation = "wt-pkg-in 500ms cubic-bezier(0.22,1,0.36,1) forwards";

    // 2. Ribbon strip pulls back (scaleX 1→0 from center)
    const t1 = setTimeout(() => {
      ribbon.style.animation = "wt-ribbon-off 400ms ease-in forwards";
    }, 700);

    // 3. Toast card pops up
    const t2 = setTimeout(() => {
      card.style.display = "flex";
      card.style.animation = "wt-card-pop 450ms cubic-bezier(0.34,1.56,0.64,1) forwards";
    }, 1050);

    // 4. Card fades, ribbon snaps back, package exits down
    const t3 = setTimeout(() => {
      card.style.animation = "wt-card-out 260ms ease-in forwards";
    }, PAUSE_MS + 1050);

    const t4 = setTimeout(() => {
      ribbon.style.animation = "wt-ribbon-on 300ms ease-out forwards";
      wrap.style.animation = "wt-pkg-out 500ms ease-in forwards";
    }, PAUSE_MS + 1050 + 260);

    const t5 = setTimeout(() => doneRef.current(), PAUSE_MS + 1050 + 260 + 500 + 150);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
  }, []);

  return createPortal(
    <div
      style={{
        position: "fixed",
        bottom: "38vh",
        right: "clamp(16px, 4vw, 80px)",
        zIndex: 99999,
        pointerEvents: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Toast card */}
      <div ref={cardRef} style={{ ...cardBase, display: "none", opacity: 0, marginBottom: 8 }}>
        <Dot />
        {message}
      </div>

      {/* Package image + ribbon overlay */}
      <div ref={wrapRef} style={{ opacity: 0, position: "relative", width: 220 }}>
        <img src={packageSrc} alt="" style={{ width: 220, height: "auto", display: "block" }} />
        {/* Ribbon strip positioned over the tape area on the box (roughly center-left of SVG) */}
        <div
          ref={ribbonRef}
          style={{
            position: "absolute",
            top: "62%",
            left: "22%",
            width: "30%",
            height: "22%",
            background: "rgba(255,255,255,0.18)",
            borderRadius: 3,
            transformOrigin: "center",
          }}
        />
      </div>
    </div>,
    document.body
  );
};

// ── Error toast ───────────────────────────────────────────
const ErrorToast: React.FC<{ t: Toast; message: string }> = ({ t, message }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      background: "#ffffff",
      borderLeft: "5px solid #DC2626",
      borderRadius: 14,
      padding: "13px 18px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.16)",
      maxWidth: 380,
      minWidth: 260,
      fontFamily: "'Poppins', sans-serif",
      opacity: t.visible ? 1 : 0,
      transition: "opacity 0.3s ease",
    }}
  >
    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#DC2626", flexShrink: 0 }} />
    <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: "#111827", lineHeight: 1.45 }}>
      {message}
    </span>
    <button
      type="button"
      onClick={() => toast.dismiss(t.id)}
      style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4, display: "flex" }}
    >
      <svg viewBox="0 0 16 16" fill="none" width="14" height="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 4L4 12M4 4l8 8" />
      </svg>
    </button>
  </div>
);

// ── Overlay store ─────────────────────────────────────────
type OverlayEntry = { id: string; type: "plane" | "package"; message: string };
type Listener = (entries: OverlayEntry[]) => void;

let _entries: OverlayEntry[] = [];
const _listeners = new Set<Listener>();

const overlayStore = {
  add(entry: OverlayEntry) {
    _entries = [..._entries, entry];
    _listeners.forEach((l) => l(_entries));
  },
  remove(id: string) {
    _entries = _entries.filter((e) => e.id !== id);
    _listeners.forEach((l) => l(_entries));
  },
  subscribe(l: Listener) {
    _listeners.add(l);
    return () => { _listeners.delete(l); };
  },
};

// ── Host component (mounted once in App.tsx) ──────────────
export const WaitlistOverlayHost: React.FC = () => {
  const [entries, setEntries] = useState<OverlayEntry[]>([]);

  useEffect(() => {
    return overlayStore.subscribe(setEntries);
  }, []);

  return (
    <>
      {entries.map((e) =>
        e.type === "plane" ? (
          <PlaneOverlay key={e.id} message={e.message} onDone={() => overlayStore.remove(e.id)} />
        ) : (
          <PackageOverlay key={e.id} message={e.message} onDone={() => overlayStore.remove(e.id)} />
        )
      )}
    </>
  );
};

// ── Public API ────────────────────────────────────────────
export const showWaitlistToast = (
  type: "success" | "error",
  message: string,
  userType?: "sender" | "traveler" | null
) => {
  if (type === "error") {
    toast.custom((t) => <ErrorToast t={t} message={message} />, { duration: 4000 });
    return;
  }
  overlayStore.add({
    id: `wt-${Date.now()}`,
    type: userType === "traveler" ? "plane" : "package",
    message,
  });
};
